
interface Vehicle {
  year: number;
  make: string;
  model: string;
  trim: string;
  vin: string;
  mileage: number;
  transmission: string;
  drivetrain: string;
  fuelType: string;
  engine: string;
  exteriorColor: string;
  interiorColor: string;
  doors: number;
  seats: number;
  mpgCity: number;
  mpgHighway: number;
  condition: string;
  titleStatus: string;
  accidents: number;
  owners: number;
}

interface VehicleSpecsProps {
  vehicle: Vehicle;
}

export default function VehicleSpecs({ vehicle }: VehicleSpecsProps) {
  const specs = [
    {
      category: 'Basic Information',
      icon: 'ri-information-line',
      color: 'blue',
      items: [
        { label: 'VIN', value: vehicle.vin, icon: 'ri-barcode-line' },
        { label: 'Year', value: vehicle.year.toString(), icon: 'ri-calendar-line' },
        { label: 'Make', value: vehicle.make, icon: 'ri-car-line' },
        { label: 'Model', value: vehicle.model, icon: 'ri-car-line' },
        { label: 'Trim', value: vehicle.trim, icon: 'ri-price-tag-3-line' },
        { label: 'Mileage', value: `${vehicle.mileage.toLocaleString()} miles`, icon: 'ri-speed-line' }
      ]
    },
    {
      category: 'Performance & Drivetrain',
      icon: 'ri-settings-3-line',
      color: 'teal',
      items: [
        { label: 'Engine', value: vehicle.engine, icon: 'ri-settings-3-line' },
        { label: 'Transmission', value: vehicle.transmission, icon: 'ri-settings-4-line' },
        { label: 'Drivetrain', value: vehicle.drivetrain, icon: 'ri-steering-2-line' },
        { label: 'Fuel Type', value: vehicle.fuelType, icon: 'ri-gas-station-line' }
      ]
    },
    {
      category: 'Fuel Economy',
      icon: 'ri-leaf-line',
      color: 'green',
      items: vehicle.mpgCity > 0 ? [
        { label: 'City MPG', value: `${vehicle.mpgCity} mpg`, icon: 'ri-map-pin-line' },
        { label: 'Highway MPG', value: `${vehicle.mpgHighway} mpg`, icon: 'ri-road-map-line' },
        { label: 'Combined', value: `${Math.round((vehicle.mpgCity + vehicle.mpgHighway) / 2)} mpg`, icon: 'ri-gas-station-line' }
      ] : [
        { label: 'Fuel Type', value: vehicle.fuelType, icon: 'ri-charging-pile-2-line' },
        { label: 'Efficiency', value: 'Electric/Hybrid', icon: 'ri-leaf-line' }
      ]
    },
    {
      category: 'Exterior & Interior',
      icon: 'ri-palette-line',
      color: 'purple',
      items: [
        { label: 'Exterior Color', value: vehicle.exteriorColor, icon: 'ri-palette-line' },
        { label: 'Interior Color', value: vehicle.interiorColor, icon: 'ri-paint-brush-line' },
        { label: 'Doors', value: vehicle.doors.toString(), icon: 'ri-door-open-line' },
        { label: 'Seats', value: vehicle.seats.toString(), icon: 'ri-user-line' }
      ]
    },
    {
      category: 'Condition & History',
      icon: 'ri-file-list-3-line',
      color: 'amber',
      items: [
        { label: 'Condition', value: vehicle.condition, icon: 'ri-star-line' },
        { label: 'Title Status', value: vehicle.titleStatus, icon: 'ri-shield-check-line' },
        { label: 'Accidents', value: vehicle.accidents === 0 ? 'None Reported' : `${vehicle.accidents} Reported`, icon: 'ri-alert-line' },
        { label: 'Previous Owners', value: vehicle.owners.toString(), icon: 'ri-user-line' }
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    const colors: { [key: string]: { bg: string; text: string; border: string } } = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
      teal: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200' },
      green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
      amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg">
          <i className="ri-file-list-3-line text-2xl text-teal-600"></i>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Detailed Specifications</h2>
      </div>
      
      <div className="space-y-6">
        {specs.map((section, sectionIndex) => {
          const colorClasses = getColorClasses(section.color);
          return (
            <div key={sectionIndex} className={`${colorClasses.bg} rounded-xl p-5 border ${colorClasses.border}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 flex items-center justify-center ${colorClasses.bg} rounded-lg border ${colorClasses.border}`}>
                  <i className={`${section.icon} text-xl ${colorClasses.text}`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {section.category}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                    <div className={`w-9 h-9 flex items-center justify-center ${colorClasses.bg} rounded-lg flex-shrink-0`}>
                      <i className={`${item.icon} ${colorClasses.text}`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 mb-0.5">{item.label}</p>
                      <p className="font-semibold text-gray-900 truncate">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Fuel Economy Visual Indicator */}
      {vehicle.mpgCity > 0 && (
        <div className="mt-6 p-5 bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
              <i className="ri-leaf-line text-xl text-green-600"></i>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-2">Fuel Efficiency Rating</h4>
              <div className="flex items-center gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">City</span>
                    <span className="text-sm font-semibold text-gray-900">{vehicle.mpgCity} MPG</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min((vehicle.mpgCity / 50) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">Highway</span>
                    <span className="text-sm font-semibold text-gray-900">{vehicle.mpgHighway} MPG</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-teal-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min((vehicle.mpgHighway / 50) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                {vehicle.mpgCity >= 30 ? 'Excellent fuel economy for daily commuting and long trips.' : 
                 vehicle.mpgCity >= 25 ? 'Good fuel economy with balanced performance.' :
                 vehicle.mpgCity >= 20 ? 'Moderate fuel economy typical for this vehicle class.' :
                 'Performance-focused with standard fuel consumption.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* VIN Highlight */}
      <div className="mt-6 p-5 bg-blue-50 border border-blue-200 rounded-xl">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
            <i className="ri-information-line text-xl text-blue-600"></i>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900 mb-2">Verify VIN Before Swap</h4>
            <p className="text-sm text-blue-800 mb-3">
              Always verify the VIN matches the title and run a vehicle history report before completing any swap. This helps ensure the vehicle's authenticity and reveals any hidden issues.
            </p>
            <div className="flex items-center gap-3">
              <div className="flex-1 text-sm font-mono text-blue-900 bg-white px-4 py-3 rounded-lg border border-blue-200">
                VIN: {vehicle.vin}
              </div>
              <button className="px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-file-copy-line mr-2"></i>
                Copy
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
